import { useCallback, useRef, useState } from 'react';
import { UploadCloud, FileText, AlertCircle } from 'lucide-react';

interface Props {
  onFile: (file: File) => void;
  isLoading?: boolean;
  error?: string | null;
}

const ACCEPT = '.csv,.xlsx';
const MAX_MB = 10;
const MAX_BYTES = MAX_MB * 1024 * 1024;

export function FileUploadZone({ onFile, isLoading, error }: Props) {
  const inputRef          = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const validate = useCallback((file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'csv' && ext !== 'xlsx') return 'Only .csv and .xlsx files are supported.';
    if (file.size > MAX_BYTES)           return `File is too large. Maximum is ${MAX_MB} MB.`;
    if (file.size === 0)                 return 'File is empty.';
    return null;
  }, []);

  const handleFile = useCallback((file: File) => {
    const err = validate(file);
    if (err) { setLocalError(err); return; }
    setLocalError(null);
    onFile(file);
  }, [onFile, validate]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }, [handleFile]);

  const shownError = localError ?? error;

  return (
    <div className="flex flex-col gap-3">
      {/* Drop zone */}
      <div
        onClick={() => !isLoading && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
        aria-label="Upload CSV or XLSX file"
        style={{
          border: `2px dashed ${dragging ? 'rgba(96,165,250,0.55)' : shownError ? 'rgba(239,68,68,0.45)' : 'rgba(96,165,250,0.22)'}`,
          borderRadius: '14px',
          background: dragging
            ? 'rgba(59,130,246,0.07)'
            : shownError
            ? 'rgba(239,68,68,0.04)'
            : 'rgba(255,255,255,0.02)',
          padding: '48px 32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 0.18s ease',
          outline: 'none',
        }}
      >
        {isLoading ? (
          <>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              border: '3px solid rgba(96,165,250,0.25)',
              borderTop: '3px solid #60a5fa',
              animation: 'spin 0.7s linear infinite',
            }} />
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, margin: 0 }}>Parsing file…</p>
          </>
        ) : (
          <>
            <div style={{
              width: 52, height: 52, borderRadius: '12px',
              background: 'rgba(59,130,246,0.12)',
              border: '1.5px solid rgba(96,165,250,0.22)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.18s ease',
              transform: dragging ? 'scale(1.08)' : 'scale(1)',
            }}>
              <UploadCloud size={24} style={{ color: '#60a5fa' }} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'rgba(255,255,255,0.80)', fontSize: 15, fontWeight: 500, margin: '0 0 4px' }}>
                {dragging ? 'Drop to upload' : 'Drag & drop your file here'}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, margin: 0 }}>
                or click to browse · CSV and XLSX · up to {MAX_MB} MB
              </p>
            </div>
          </>
        )}
      </div>

      {/* Error message */}
      {shownError && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(239,68,68,0.10)',
          border: '1px solid rgba(239,68,68,0.28)',
          borderRadius: 10, padding: '10px 14px',
          color: '#fca5a5', fontSize: 13.5,
        }}>
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{shownError}</span>
        </div>
      )}

      {/* Format chips */}
      <div style={{ display: 'flex', gap: 8 }}>
        {['.csv', '.xlsx'].map(fmt => (
          <div key={fmt} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(96,165,250,0.10)',
            borderRadius: 7, padding: '4px 10px',
            fontSize: 12, color: 'rgba(255,255,255,0.38)',
          }}>
            <FileText size={11} style={{ color: '#60a5fa' }} />
            {fmt}
          </div>
        ))}
      </div>

      <input ref={inputRef} type="file" accept={ACCEPT} onChange={onInputChange} style={{ display: 'none' }} />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
